import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ActiveOfferData {
  offerId: string;
  orderId: string;
  orderTotal: number;
  discountType: string;
  discountValue: number;
  maxDiscountAmount?: number;
  titleAr: string;
  titleEn: string;
  backgroundColor: string;
  textColor: string;
  expiresAt: number; // timestamp in ms
  mergeTimeMinutes: number;
}

interface ActiveOfferState {
  activeOffer: ActiveOfferData | null;
  usedPopupForOrders: string[]; // Track orders where popup was used and order was added
  usedStickyBannerForOrders: string[]; // Track orders where sticky banner was used
  setActiveOffer: (offer: ActiveOfferData) => void;
  clearActiveOffer: () => void;
  isOfferValid: () => boolean;
  getTimeRemaining: () => number;
  markPopupUsed: (orderId: string) => void;
  hasUsedPopup: (orderId: string) => boolean;
  markStickyBannerUsed: (orderId: string) => void;
  hasUsedStickyBanner: (orderId: string) => boolean;
}

export const useActiveOfferStore = create<ActiveOfferState>()(
  persist(
    (set, get) => ({
      activeOffer: null,
      usedPopupForOrders: [],
      usedStickyBannerForOrders: [],
      
      setActiveOffer: (offer) => {
        set({ activeOffer: offer });
      },
      
      clearActiveOffer: () => {
        set({ activeOffer: null });
      },
      
      isOfferValid: () => {
        const offer = get().activeOffer;
        if (!offer) return false;
        return Date.now() < offer.expiresAt;
      },
      
      getTimeRemaining: () => {
        const offer = get().activeOffer;
        if (!offer) return 0;
        return Math.max(0, offer.expiresAt - Date.now());
      },
      
      markPopupUsed: (orderId: string) => {
        const current = get().usedPopupForOrders;
        if (!current.includes(orderId)) {
          set({ usedPopupForOrders: [...current, orderId] });
        }
      },
      
      hasUsedPopup: (orderId: string) => {
        return get().usedPopupForOrders.includes(orderId);
      },
      
      markStickyBannerUsed: (orderId: string) => {
        const current = get().usedStickyBannerForOrders;
        if (!current.includes(orderId)) {
          set({ usedStickyBannerForOrders: [...current, orderId] });
        }
      },
      
      hasUsedStickyBanner: (orderId: string) => {
        return get().usedStickyBannerForOrders.includes(orderId);
      },
    }),
    {
      name: 'active-offer-storage',
      partialize: (state) => ({ 
        activeOffer: state.activeOffer,
        usedPopupForOrders: state.usedPopupForOrders,
        usedStickyBannerForOrders: state.usedStickyBannerForOrders,
      }),
    }
  )
);
